import type {
  ContactFilterQuery,
  ContactFilterRule,
  ConversationFilterFieldName,
  FilterCombinator,
  FilterRow,
} from "@/features/inbox/types/filter";

import {
  countRules,
  isRuleValueValid,
} from "@/components/contact-query-builder/query-builder.helpers";

export const EMPTY_FILTER: ContactFilterQuery = { rules: [] };

export type OperatorValueKind =
  | "none"
  | "scalar"
  | "array"
  | "range"
  | "relative";

/** The shape of value an operator expects, used to know when to reset on operator change. */
export const operatorValueKind = (operator: string): OperatorValueKind => {
  if (["exists", "doesNotExist", "null", "notNull"].includes(operator)) {
    return "none";
  }
  if (operator === "between") return "range";
  if (operator === "inTheLast" || operator === "moreThanAgo") return "relative";
  if (operator === "hasAnyOf" || operator === "hasNoneOf") return "array";

  return "scalar";
};

/** Initial value for a freshly-selected operator kind (array starts empty, rest cleared). */
export const emptyValueForOperator = (operator: string): unknown =>
  operatorValueKind(operator) === "array" ? [] : undefined;

const createRowId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `row-${Math.round(performance.now() * 1000)}`;

/** A row is complete when its field/operator are set and the value fits the operator. */
export const isRowValid = (row: FilterRow): boolean =>
  Boolean(row.field && row.operator) &&
  isRuleValueValid(row.operator, row.value);

export const createFilterRow = (
  field: ConversationFilterFieldName,
  operator: string,
  value: unknown = undefined,
  combinator: FilterCombinator = "and",
): FilterRow => ({ id: createRowId(), field, operator, value, combinator });

/**
 * Flatten a stored filter group into editable rows. Inbox filters are always a
 * flat AND-list, so interleaved combinator strings and any nested groups are
 * dropped (the popover never produces them).
 */
export const ruleGroupToRows = (
  group: ContactFilterQuery | null | undefined,
): FilterRow[] => {
  if (!group?.rules?.length) return [];

  const rows: FilterRow[] = [];
  // The combinator preceding the next rule. IC arrays interleave the operator
  // string between rules, so we carry it forward onto the row it joins.
  let combinator: FilterCombinator = "and";

  for (const item of group.rules) {
    if (typeof item === "string") {
      combinator = item === "or" ? "or" : "and";
      continue;
    }

    if ("rules" in item) continue;

    const rule = item as ContactFilterRule;

    if (!rule.field || !rule.operator) continue;

    rows.push({
      // Reuse the stored rule's id rather than minting a fresh one: a controlled
      // react-querybuilder query whose rules lack stable ids remounts forever.
      id: rule.id ?? createRowId(),
      field: rule.field as ConversationFilterFieldName,
      operator: rule.operator,
      value: rule.value,
      combinator,
    });

    combinator = "and";
  }

  return rows;
};

/**
 * Serialize valid rows back into a `RuleGroupTypeIC`, interleaving the implicit
 * "and" combinator the backend expects.
 */
export const rowsToRuleGroup = (rows: FilterRow[]): ContactFilterQuery => {
  const validRows = rows.filter(isRowValid);
  const rules: Array<ContactFilterRule | string> = [];

  validRows.forEach((row, index) => {
    if (index > 0) rules.push(row.combinator === "or" ? "or" : "and");

    rules.push({
      id: row.id,
      field: row.field,
      operator: row.operator,
      value: row.value,
    });
  });

  return { rules } as ContactFilterQuery;
};

export const countActiveRules = (
  group: ContactFilterQuery | null | undefined,
): number => (group ? countRules(group) : 0);

/**
 * The values currently set for a single field, normalized to a string array.
 * Lets quick-toggle UIs (e.g. the sidebar nav tree) read one field out of the
 * shared filter without caring about rule/operator structure.
 */
export const getFieldArrayValue = (
  filter: ContactFilterQuery | null | undefined,
  fieldName: ConversationFilterFieldName,
): string[] => {
  const row = ruleGroupToRows(filter).find((r) => r.field === fieldName);

  if (!row) return [];
  if (Array.isArray(row.value)) return row.value as string[];
  if (typeof row.value === "string" && row.value) return [row.value];

  return [];
};

/**
 * Replace (or remove, when empty) the rule for a single field while preserving
 * every other rule. Used by quick-toggle UIs so they edit the same filter the
 * popover does.
 */
export const upsertFieldArrayRule = (
  filter: ContactFilterQuery | null | undefined,
  fieldName: ConversationFilterFieldName,
  values: string[],
  operator: string = "hasAnyOf",
): ContactFilterQuery => {
  const rows = ruleGroupToRows(filter).filter((r) => r.field !== fieldName);

  if (values.length > 0) {
    rows.push(createFilterRow(fieldName, operator, values));
  }

  return rowsToRuleGroup(rows);
};

/** Whether a boolean field (e.g. `draftPending`) is currently filtered to true. */
export const isBooleanFieldActive = (
  filter: ContactFilterQuery | null | undefined,
  fieldName: ConversationFilterFieldName,
): boolean =>
  ruleGroupToRows(filter).some(
    (r) => r.field === fieldName && r.value === true,
  );

/**
 * Boolean counterpart to {@link upsertFieldArrayRule}: set the field's rule to
 * `true`, or drop it entirely when toggled off (an explicit `false` rule would
 * mean "only contacts where this is false", which is not what a quick filter
 * expresses). Every other rule is preserved.
 */
export const upsertBooleanRule = (
  filter: ContactFilterQuery | null | undefined,
  fieldName: ConversationFilterFieldName,
  active: boolean,
): ContactFilterQuery => {
  const rows = ruleGroupToRows(filter).filter((r) => r.field !== fieldName);

  if (active) {
    rows.push(createFilterRow(fieldName, "isEqualTo", true));
  }

  return rowsToRuleGroup(rows);
};

/**
 * Radio counterpart to {@link upsertBooleanRule}: applies at most one rule out
 * of `fieldNames`, dropping the rest. The sidebar's Echo AI rows read as views
 * rather than checkboxes, so selecting one has to replace its siblings instead
 * of ANDing with them, and `null` clears the whole family. Rules outside
 * `fieldNames` are preserved.
 */
export const setExclusiveBooleanRule = (
  filter: ContactFilterQuery | null | undefined,
  fieldNames: ConversationFilterFieldName[],
  selected: ConversationFilterFieldName | null,
): ContactFilterQuery => {
  const rows = ruleGroupToRows(filter).filter(
    (r) => !fieldNames.includes(r.field as ConversationFilterFieldName),
  );

  if (selected) {
    rows.push(createFilterRow(selected, "isEqualTo", true));
  }

  return rowsToRuleGroup(rows);
};
